// utils/serviceWorkerRegistration.ts - Service Worker registration and management
// Enhanced with Safari compatibility and better cache management

import logger from './logger';

const isLocalhost = Boolean(
  window.location.hostname === 'localhost' ||
  window.location.hostname === '[::1]' ||
  window.location.hostname.match(/^127(?:\.(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)){3}$/)
);

// Detect Safari browser
const isSafari = () => {
  const ua = navigator.userAgent.toLowerCase();
  return ua.includes('safari') && !ua.includes('chrome') && !ua.includes('chromium');
};

// Detect iOS Safari
const isIOSSafari = () => {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream;
};

type Config = {
  onSuccess?: (registration: ServiceWorkerRegistration) => void;
  onUpdate?: (registration: ServiceWorkerRegistration) => void;
  onError?: (error: Error) => void;
};

// Store service worker version in localStorage for tracking
const SW_VERSION_KEY = 'valulearn_sw_version';

export function register(config?: Config) {
  if ('serviceWorker' in navigator) {
    // Special handling for Safari
    if (isSafari() || isIOSSafari()) {
      logger.debug('[ServiceWorker] Safari detected, using enhanced registration');

      // Only clear caches if there's a version mismatch (not on every load)
      // This prevents constant refreshing
      // clearSafariCachesIfNeeded();
    }
    
    const publicUrl = new URL(import.meta.env?.BASE_URL || '', window.location.href);
    if (publicUrl.origin !== window.location.origin) {
      return;
    }

    window.addEventListener('load', () => {
      const baseUrl = import.meta.env?.BASE_URL || '/';
      const swUrl = baseUrl.endsWith('/') ? `${baseUrl}service-worker.js` : `${baseUrl}/service-worker.js`;
      
      // Add timestamp to force Safari to check for updates
      const swUrlWithTimestamp = isSafari() 
        ? `${swUrl}?t=${Date.now()}`
        : swUrl;

      if (isLocalhost) {
        checkValidServiceWorker(swUrlWithTimestamp, config);
        navigator.serviceWorker.ready.then(() => {
          logger.debug('[ServiceWorker] App served cache-first by service worker');
        });
      } else {
        registerValidSW(swUrlWithTimestamp, config);
      }
    });
    
    // Listen for service worker updates
    listenForServiceWorkerUpdates();
  }
}

function registerValidSW(swUrl: string, config?: Config) {
  navigator.serviceWorker
    .register(swUrl, {
      // Force update check for Safari
      updateViaCache: isSafari() ? 'none' : 'imports'
    })
    .then((registration) => {
      // Don't force immediate update check to prevent loops
      // if (isSafari()) {
      //   registration.update();
      // }
      
      registration.onupdatefound = () => {
        const installingWorker = registration.installing;
        if (installingWorker == null) {
          return;
        }
        
        installingWorker.onstatechange = () => {
          if (installingWorker.state === 'installed') {
            if (navigator.serviceWorker.controller) {
              logger.info('[ServiceWorker] New content available; please refresh');

              // Store the new version
              getServiceWorkerVersion().then(version => {
                if (version) {
                  localStorage.setItem(SW_VERSION_KEY, version);
                }
              });
              
              if (config && config.onUpdate) {
                config.onUpdate(registration);
              }
              
              // Update notification disabled
              // showUpdateNotification();
            } else {
              logger.info('[ServiceWorker] Content cached for offline use');

              if (config && config.onSuccess) {
                config.onSuccess(registration);
              }
            }
          }
        };
      };
      
      // Check for updates periodically (especially for Safari)
      // Only check every 30 minutes to avoid excessive refreshing
      if (isSafari() || isIOSSafari()) {
        setInterval(() => {
          registration.update();
        }, 1800000); // Check every 30 minutes
      }
    })
    .catch((error) => {
      logger.error('[ServiceWorker] Registration error:', error);

      // Don't auto-retry on Safari to prevent refresh loops
      // Just log the error and let the user manually refresh if needed

      if (config && config.onError) {
        config.onError(error);
      }
    });
}

function checkValidServiceWorker(swUrl: string, config?: Config) {
  fetch(swUrl, {
    headers: { 
      'Service-Worker': 'script',
      // Force Safari to bypass cache
      'Cache-Control': 'no-cache, no-store, must-revalidate'
    },
  })
    .then((response) => {
      const contentType = response.headers.get('content-type');
      if (
        response.status === 404 ||
        (contentType != null && contentType.indexOf('javascript') === -1)
      ) {
        navigator.serviceWorker.ready.then((registration) => {
          registration.unregister().then(() => {
            window.location.reload();
          });
        });
      } else {
        registerValidSW(swUrl, config);
      }
    })
    .catch(() => {
      logger.info('[ServiceWorker] No internet connection. Running in offline mode');
    });
}

export function unregister() {
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.ready
      .then((registration) => {
        registration.unregister();
        // Clear all caches when unregistering
        clearAllCaches();
      })
      .catch((error) => {
        console.error(error.message);
      });
  }
}

// Helper function to show update notification (disabled)
function showUpdateNotification() {
  return; // Disabled - don't show update notifications
  // Remove any existing notification
  const existingNotification = document.querySelector('.sw-update-notification');
  if (existingNotification) {
    existingNotification.remove();
  }
  
  const notification = document.createElement('div');
  notification.className = 'sw-update-notification';
  notification.innerHTML = `
    <div style="
      position: fixed;
      bottom: 20px;
      right: 20px;
      background: #6CA6C3;
      color: white;
      padding: 16px 20px;
      border-radius: 8px;
      box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
      z-index: 9999;
      max-width: 320px;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
    ">
      <div style="font-weight: 600; margin-bottom: 4px;">Update Available</div>
      <div style="font-size: 14px; margin-bottom: 12px;">
        ${isSafari() ? 'Safari will refresh automatically in a few seconds...' : 'New version is ready. Please refresh to update.'}
      </div>
      <button onclick="window.location.reload(true)" style="
        background: white;
        color: #6CA6C3;
        border: none;
        padding: 8px 16px;
        border-radius: 4px;
        font-weight: 600;
        cursor: pointer;
      ">Refresh Now</button>
      <button onclick="this.parentElement.parentElement.remove()" style="
        background: transparent;
        color: white;
        border: 1px solid white;
        padding: 8px 16px;
        border-radius: 4px;
        margin-left: 8px;
        cursor: pointer;
      ">Later</button>
    </div>
  `;
  document.body.appendChild(notification);
}

// Listen for service worker update messages
function listenForServiceWorkerUpdates() {
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.addEventListener('message', (event) => {
      if (event.data.type === 'SERVICE_WORKER_UPDATED') {
        logger.info('[ServiceWorker] Received update message, version:', event.data.version);

        // Store the new version
        localStorage.setItem(SW_VERSION_KEY, event.data.version);
        
        // Don't automatically clear caches, just show notification
        // User can manually clear if needed via the cache manager
      }
    });
  }
}

// Get service worker version
async function getServiceWorkerVersion(): Promise<string | null> {
  if (!('serviceWorker' in navigator)) return null;
  
  try {
    const registration = await navigator.serviceWorker.ready;
    const messageChannel = new MessageChannel();
    
    return new Promise((resolve) => {
      messageChannel.port1.onmessage = (event) => {
        resolve(event.data.version);
      };
      
      registration.active?.postMessage(
        { type: 'GET_VERSION' },
        [messageChannel.port2]
      );
      
      // Timeout after 2 seconds
      setTimeout(() => resolve(null), 2000);
    });
  } catch {
    return null;
  }
}

// Clear Safari caches if version mismatch (kept for manual use via cache manager)
// Not called automatically to prevent refresh loops
// async function clearSafariCachesIfNeeded() {
//   const storedVersion = localStorage.getItem(SW_VERSION_KEY);
//   const currentVersion = await getServiceWorkerVersion();
//   
//   if (storedVersion && currentVersion && storedVersion !== currentVersion) {
//     console.log('[ServiceWorker] Version mismatch detected, clearing Safari caches');
//     await clearSafariCaches();
//     localStorage.setItem(SW_VERSION_KEY, currentVersion);
//   }
// }

// Clear Safari-specific caches
async function clearSafariCaches() {
  try {
    // Clear all caches
    await clearAllCaches();
    
    // Clear localStorage items that might cause issues
    const keysToKeep = ['valulearn_user', 'valulearn_token', 'valulearn_theme'];
    const allKeys = Object.keys(localStorage);
    
    allKeys.forEach(key => {
      if (!keysToKeep.some(keepKey => key.includes(keepKey))) {
        localStorage.removeItem(key);
      }
    });
    
    // Clear sessionStorage
    sessionStorage.clear();
    
    logger.info('[ServiceWorker] Safari caches cleared');
  } catch (error) {
    logger.error('[ServiceWorker] Failed to clear Safari caches:', error);
  }
}

// Clear all caches
async function clearAllCaches() {
  if ('caches' in window) {
    const cacheNames = await caches.keys();
    await Promise.all(cacheNames.map(name => caches.delete(name)));
    logger.info('[ServiceWorker] All caches cleared');
  }
}

// Cache management utilities
export const cacheUtils = {
  // Clear all caches
  clearAllCaches,
  
  // Clear Safari caches
  clearSafariCaches,
  
  // Get cache size
  getCacheSize: async () => {
    if ('storage' in navigator && 'estimate' in navigator.storage) {
      const { usage, quota } = await navigator.storage.estimate();
      return {
        usage: usage || 0,
        quota: quota || 0,
        percentage: ((usage || 0) / (quota || 1)) * 100
      };
    }
    return null;
  },
  
  // Force service worker update
  forceUpdate: async () => {
    if ('serviceWorker' in navigator) {
      const registration = await navigator.serviceWorker.ready;
      await registration.update();
      
      // For Safari, also clear caches
      if (isSafari() || isIOSSafari()) {
        await clearSafariCaches();
        window.location.reload();
      }
    }
  },
  
  // Check if update is available
  checkForUpdate: async () => {
    if ('serviceWorker' in navigator) {
      try {
        const registration = await navigator.serviceWorker.ready;
        await registration.update();
        return true;
      } catch {
        return false;
      }
    }
    return false;
  },
  
  // Preload critical resources
  preloadResources: async (urls: string[]) => {
    const cache = await caches.open('valulearn-preload');
    await cache.addAll(urls);
  }
};

// Background sync utilities
export const syncUtils = {
  // Register background sync
  registerSync: async (tag: string) => {
    const registration = await navigator.serviceWorker.ready;
    if ('sync' in registration) {
      await (registration as any).sync.register(tag);
    }
  },
  
  // Check if background sync is supported
  isSupported: () => {
    return 'serviceWorker' in navigator && 'SyncManager' in window;
  }
};

// Push notification utilities
export const notificationUtils = {
  // Request notification permission
  requestPermission: async () => {
    if ('Notification' in window) {
      const permission = await Notification.requestPermission();
      return permission === 'granted';
    }
    return false;
  },
  
  // Subscribe to push notifications
  subscribeToPush: async () => {
    const registration = await navigator.serviceWorker.ready;
    
    if ('pushManager' in registration) {
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: import.meta.env?.VITE_VAPID_PUBLIC_KEY || ''
      });
      
      // Send subscription to server
      await fetch('/api/push/subscribe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(subscription)
      });
      
      return subscription;
    }
    return null;
  },
  
  // Check notification permission status
  getPermissionStatus: () => {
    if ('Notification' in window) {
      return Notification.permission;
    }
    return 'denied';
  }
};

// Safari-specific utilities
export const safariUtils = {
  isSafari,
  isIOSSafari,
  
  // Force refresh for Safari
  forceRefresh: () => {
    if (isSafari() || isIOSSafari()) {
      // Clear caches first
      clearSafariCaches().then(() => {
        // Use location.replace for hard refresh in Safari
        window.location.replace(window.location.href);
      });
    } else {
      window.location.reload();
    }
  },
  
  // Check if running as standalone PWA on iOS
  isStandalone: () => {
    return (window.navigator as any).standalone === true ||
           window.matchMedia('(display-mode: standalone)').matches;
  }
};